import React, { useEffect, useState } from 'react';
import { Layers, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const SCALE = [1, 2, 3, 4, 5];
const burden = (c, s) => Math.round((c + s - 2) / 8 * 100);

// Managers set role complexity + stakes per cohort → context burden default for Expected Confidence.
export default function CohortContextDialog({ open, onOpenChange, organizationId, onSaved }) {
  const [rows, setRows] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);
  const orgQ = organizationId && organizationId !== '_all' ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
  const load = () => api.get(`/transfer-health/cohort-context${orgQ}`).then((r) => { setRows(r.data.cohorts); setEdits({}); }).catch((e) => toast.error(e.response?.data?.detail || e.message));
  useEffect(() => { if (open) load(); }, [open, organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const val = (c, k) => edits[c.id]?.[k] ?? c.context?.[k] ?? 3;
  const save = async (c) => {
    setBusy(c.id);
    try {
      const body = { complexity: val(c, 'complexity'), stakes: val(c, 'stakes') };
      if (organizationId && organizationId !== '_all') body.organization_id = organizationId;
      await api.put(`/transfer-health/cohort-context/${c.id}`, body);
      toast.success(`${c.name}: context burden ${burden(body.complexity, body.stakes)}`);
      await load(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const clear = async (c) => {
    const body = { clear: true };
    if (organizationId && organizationId !== '_all') body.organization_id = organizationId;
    await api.put(`/transfer-health/cohort-context/${c.id}`, body); await load(); onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cohort-context-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Layers className="h-4 w-4" /> Role context per cohort</DialogTitle>
          <DialogDescription className="text-xs">Complexity and stakes (1 low → 5 high) become the cohort&apos;s <strong>context burden</strong> (0–100), used in Expected Confidence whenever a learner row has no context value of its own. Higher burden legitimately lowers the confidence we expect.</DialogDescription>
        </DialogHeader>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <table className="w-full text-xs" data-testid="cohort-context-table">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="text-left py-1">Cohort</th><th>Complexity</th><th>Stakes</th><th>Burden</th><th /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <tr key={c.id} data-testid={`cohort-context-${c.id}`}>
                  <td className="py-1.5 pr-2 text-slate-800">{c.name}{c.context?.set_by && <p className="text-slate-400">set by {c.context.set_by}</p>}</td>
                  {['complexity', 'stakes'].map((k) => (
                    <td key={k} className="py-1.5 text-center">
                      <select value={val(c, k)} onChange={(e) => setEdits({ ...edits, [c.id]: { ...edits[c.id], [k]: Number(e.target.value) } })} className="h-8 rounded-md border border-slate-300 px-2" data-testid={`cohort-context-${k}-${c.id}`}>
                        {SCALE.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </td>
                  ))}
                  <td className="py-1.5 text-center font-semibold tabular-nums" data-testid={`cohort-context-burden-${c.id}`}>{c.context ? c.context.burden : <span className="text-slate-400 font-normal">default</span>}{edits[c.id] && <span className="text-slate-400 font-normal"> → {burden(val(c, 'complexity'), val(c, 'stakes'))}</span>}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {c.context && <Button size="sm" variant="ghost" onClick={() => clear(c)} data-testid={`cohort-context-clear-${c.id}`}>Clear</Button>}
                    <Button size="sm" onClick={() => save(c)} disabled={busy === c.id} className="bg-teal-600 hover:bg-teal-700 ml-1" data-testid={`cohort-context-save-${c.id}`}>{busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
